import { z } from "zod";
import {
  CreateStatusParameterSchema,
  CreateNumericParameterSchema,
  CreateCounterParameterSchema,
  CreateBooleanParameterSchema,
  CreateTextParameterSchema,
  CreateDurationParameterSchema,
  UpdateParameterRequestBaseSchema,
  limitsRefine,
  LIMITS_REFINE_MESSAGE,
  RegisterParameterEntryRequestSchema,
  RegisterStatusEntryRequestSchema,
} from "./parameters.js";
import { type CommandSpec, Uuid, hasUpdate, UPDATE_MSG } from "./assistant-catalog-types.js";

// Família Parâmetros & apontamentos. CRUD de parâmetro de máquina (config, GESTOR)
// + apontamentos operacionais (medição/status, OPERADOR). Cada comando reusa EXATAMENTE o service da
// aba Parâmetros / bloco Operação da sidebar da máquina via dispatch (zero lógica nova). Args reusam
// os request schemas (.extend + .strict()) p/ não divergir do wire. `parameter.delete` é DESTRUTIVA
// (confirmação por nome). NÃO confundir com `scenario.setMachineSimParams` (parâmetro de SIMULAÇÃO de
// cenário, família Config) — aqui é o parâmetro medível da máquina real.

// `parameter.create` reusa a união discriminada do wire (uma por categoria), injetando machineId em
// cada membro. Manter a lista alinhada a CreateParameterRequestSchema (mesmas 6 categorias).
const ParameterCreateArgs = z.discriminatedUnion("category", [
  CreateStatusParameterSchema.extend({ machineId: Uuid }).strict(),
  CreateNumericParameterSchema.extend({ machineId: Uuid }).strict(),
  CreateCounterParameterSchema.extend({ machineId: Uuid }).strict(),
  CreateBooleanParameterSchema.extend({ machineId: Uuid }).strict(),
  CreateTextParameterSchema.extend({ machineId: Uuid }).strict(),
  CreateDurationParameterSchema.extend({ machineId: Uuid }).strict(),
]);

export const PARAMS_COMMANDS: readonly CommandSpec[] = [
  {
    name: "parameter.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description:
      "Cria um parâmetro medível de uma máquina (categorias STATUS/NUMERIC/COUNTER/BOOLEAN/TEXT/DURATION). Parâmetro da MÁQUINA REAL — não é ajuste de simulação de cenário.",
    argsSchema: ParameterCreateArgs,
  },
  {
    name: "parameter.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description:
      "Edita SÓ os campos informados de um parâmetro de máquina. A categoria é imutável — não a envie. Omitir um campo o mantém.",
    // Spec 078 — o schema HTTP é o Base + refine(min<max); aqui o refine entra depois do extend
    // (ZodEffects não tem .extend). Campos novos: visualRole, limitMax, limitMin.
    argsSchema: UpdateParameterRequestBaseSchema.extend({ machineId: Uuid, parameterId: Uuid })
      .strict()
      .refine(hasUpdate(["machineId", "parameterId"]), UPDATE_MSG)
      .refine(limitsRefine, LIMITS_REFINE_MESSAGE),
  },
  {
    name: "parameter.delete",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Exclui um parâmetro de máquina (bloqueado se houver sensor vinculado).",
    argsSchema: z.object({ machineId: Uuid, parameterId: Uuid }).strict(),
  },
  {
    name: "parameter.recordEntry",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description:
      "Registra AGORA uma medição manual de um parâmetro já configurado (inclui contador de produção). Não use em parâmetro STATUS (use machine.recordStatus).",
    argsSchema: RegisterParameterEntryRequestSchema.extend({
      lineId: Uuid,
      machineId: Uuid,
      parameterId: Uuid,
    }).strict(),
  },
  {
    name: "machine.recordStatus",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description:
      "Registra manualmente o status de uma máquina. `value` deve ser uma das CHAVES do statusMapping do parâmetro STATUS da máquina (rótulos configurados, ex.: 'Parada', 'Rodando') — descubra-as via machine_detail; NÃO envie o status canônico (STOPPED/RUNNING/IDLE/NODATA). occurredAt não pode ser futuro nem retroativo além de 24h.",
    argsSchema: RegisterStatusEntryRequestSchema.extend({ lineId: Uuid, machineId: Uuid }).strict(),
  },
];
